import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import Badge from '@/shared/ui/components/basic/Badge';
import { cn } from '@/shared/ui/utils/tailwind';
import { countPhotographedRooms } from '@/features/vistories/utils/vistory-progress';
import type { Vistory } from '../types/vistory.types';
import { VistoryDetailPanel } from './VistoryDetailPanel';

interface Props {
  vistory: Vistory;
  agencyId: string;
  onUpdate: () => void;
}

const STATUS_BADGE: Record<string, { color: string; bg: string }> = {
  draft: { color: 'var(--status-info)', bg: 'var(--status-info-subtle)' },
  pending_approval: { color: 'var(--status-warning)', bg: 'var(--status-warning-subtle)' },
  approved: { color: 'var(--status-success)', bg: 'var(--status-success-subtle)' },
  rejected: { color: 'var(--status-error)', bg: 'var(--status-error-subtle)' },
};

/**
 * Clickable summary card for a single vistory that expands its detail panel.
 */
export function VistoryCard({ vistory, agencyId, onUpdate }: Props) {
  const { t } = useTranslation('features/vistories');
  const [expanded, setExpanded] = useState(false);
  const tone = STATUS_BADGE[vistory.status] ?? STATUS_BADGE.draft;
  const rooms = vistory.rooms ?? [];
  const progress = countPhotographedRooms(rooms);
  const createdDate = formatLocalizedDate(vistory.created_at);

  return (
    <>
      <Text
        as="button"
        type="button"
        onClick={() => setExpanded(true)}
        className={cn(
          'flex flex-col gap-[var(--space-2)] rounded-(--radius-lg) border border-[var(--border-subtle)]',
          'bg-[var(--surface-primary)] p-[var(--space-4)] shadow-[var(--shadow-sm)] text-left',
          'transition-shadow duration-150 hover:shadow-[var(--shadow-md)]',
        )}
      >
        <div className="flex items-center justify-between gap-[var(--space-2)]">
          <Text span size="sm" fw={500} className="text-[var(--text-primary)] truncate">
            {vistory.title}
          </Text>
          <div className="flex items-center gap-[var(--space-1)] shrink-0">
            {vistory.type && (
              <Badge color="var(--text-secondary)" backgroundColor="var(--surface-tertiary)" size="sm">
                {t(`vistory.type_${vistory.type}`)}
              </Badge>
            )}
            <Badge color={tone.color} backgroundColor={tone.bg} size="sm">
              {t(`vistory.status.${vistory.status}`)}
            </Badge>
          </div>
        </div>
        <Text span size="xs" className="text-[var(--text-secondary)]">
          {createdDate} · {vistory.photos.length} {t('vistory.photos_count')} · {progress.photographed}/{progress.total} {t('vistory.rooms_photographed')}
        </Text>
      </Text>
      {expanded && (
        <VistoryDetailPanel
          vistory={vistory}
          agencyId={agencyId}
          onClose={() => setExpanded(false)}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
}
